import CalendarAPI from '../../../api/calendar';

// LocalStorage key for persisting calendar/agent selection
const SELECTED_CALENDARS_KEY = 'chatwoot_selected_calendar_ids';
const SELECTED_AGENTS_KEY = 'chatwoot_selected_agent_ids';

// Helper to load from localStorage
const loadFromStorage = (key, defaultValue = []) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper to save to localStorage
const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

const state = {
  calendars: [],
  currentCalendar: null,
  selectedCalendarIds: loadFromStorage(SELECTED_CALENDARS_KEY),
  selectedAgentIds: loadFromStorage(SELECTED_AGENTS_KEY),
  appointmentTypes: [],
  reminders: [],
  appointments: [],
  currentDate: new Date().toISOString().split('T')[0],
  currentView: 'week', // day, week, month
  availableSlots: [],
  elevenLabsVoices: [],
  uiFlags: {
    isFetchingCalendars: false,
    isFetchingCalendar: false,
    isFetchingAppointmentTypes: false,
    isFetchingAppointments: false,
    isFetchingAvailability: false,
    isFetchingVoices: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getCalendars: $state => $state.calendars,
  getCurrentCalendar: $state => $state.currentCalendar,
  getSelectedCalendarIds: $state => $state.selectedCalendarIds,
  getSelectedAgentIds: $state => $state.selectedAgentIds,
  getAppointmentTypes: $state => $state.appointmentTypes,
  getActiveAppointmentTypes: $state =>
    $state.appointmentTypes.filter(t => t.active),
  getReminders: $state => $state.reminders,
  getElevenLabsVoices: $state => $state.elevenLabsVoices,
  getAppointments: $state => $state.appointments,
  getCurrentDate: $state => $state.currentDate,
  getCurrentView: $state => $state.currentView,
  getAvailableSlots: $state => $state.availableSlots,
  getUIFlags: $state => $state.uiFlags,

  // Get filtered appointments based on selected calendars and agents
  getFilteredAppointments: $state => {
    // If no calendars are selected, return empty array (show no cards)
    if ($state.selectedCalendarIds.length === 0) {
      return [];
    }

    let filtered = $state.appointments;

    // Filter by selected calendars
    filtered = filtered.filter(apt => {
      const calendarId = apt.calendar?.id;
      return $state.selectedCalendarIds.includes(calendarId);
    });

    // Filter by selected agents (if any are selected, empty means all)
    if ($state.selectedAgentIds.length > 0) {
      filtered = filtered.filter(apt =>
        apt.agent?.id ? $state.selectedAgentIds.includes(apt.agent.id) : true
      );
    }

    return filtered;
  },

  // Get appointment count per calendar
  getAppointmentCountByCalendar: $state => {
    const counts = {};
    $state.appointments.forEach(apt => {
      const calId = apt.calendar?.id;
      if (calId) {
        counts[calId] = (counts[calId] || 0) + 1;
      }
    });
    return counts;
  },

  // Get appointments for a specific date
  getAppointmentsByDate: $state => date => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return $state.appointments.filter(apt => {
      const aptDate = apt.starts_at.split('T')[0];
      return aptDate === dateStr;
    });
  },

  // Get appointments for a specific agent
  getAppointmentsByAgent: $state => agentId =>
    $state.appointments.filter(apt => apt.agent?.id === agentId),

  // Get upcoming appointments (next 7 days)
  getUpcomingAppointments: $state => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return $state.appointments
      .filter(apt => {
        const aptDate = new Date(apt.starts_at);
        return aptDate >= now && aptDate <= weekFromNow;
      })
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  },

  // Get today's appointments
  getTodayAppointments: $state => {
    const today = new Date().toISOString().split('T')[0];
    return $state.appointments
      .filter(apt => apt.starts_at.split('T')[0] === today)
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  },
};

const actions = {
  // Calendars
  async fetchCalendars({ commit }) {
    commit('setUIFlag', { isFetchingCalendars: true });
    try {
      const response = await CalendarAPI.getCalendars();
      commit('setCalendars', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingCalendars: false });
    }
  },

  async fetchCalendar({ commit }, calendarId) {
    commit('setUIFlag', { isFetchingCalendar: true });
    try {
      const response = await CalendarAPI.getCalendar(calendarId);
      commit('setCurrentCalendar', response.data);
      commit('setAppointmentTypes', response.data.appointment_types || []);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingCalendar: false });
    }
  },

  async createCalendar({ commit }, data) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await CalendarAPI.createCalendar(data);
      commit('addCalendar', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateCalendar({ commit }, { calendarId, data }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await CalendarAPI.updateCalendar(calendarId, data);
      commit('updateCalendar', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteCalendar({ commit }, calendarId) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await CalendarAPI.deleteCalendar(calendarId);
      commit('removeCalendar', calendarId);
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  // Appointment Types
  async fetchAppointmentTypes({ commit }, calendarId) {
    commit('setUIFlag', { isFetchingAppointmentTypes: true });
    try {
      const response = await CalendarAPI.getAppointmentTypes(calendarId);
      commit('setAppointmentTypes', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingAppointmentTypes: false });
    }
  },

  async createAppointmentType({ commit }, { calendarId, data }) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await CalendarAPI.createAppointmentType(calendarId, data);
      commit('addAppointmentType', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateAppointmentType({ commit }, { calendarId, typeId, data }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await CalendarAPI.updateAppointmentType(
        calendarId,
        typeId,
        data
      );
      commit('updateAppointmentType', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteAppointmentType({ commit }, { calendarId, typeId }) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await CalendarAPI.deleteAppointmentType(calendarId, typeId);
      commit('removeAppointmentType', typeId);
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  // Reminders
  async fetchReminders({ commit }, calendarId) {
    try {
      const response = await CalendarAPI.getReminders(calendarId);
      commit('setReminders', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async createReminder({ commit }, { calendarId, data }) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await CalendarAPI.createReminder(calendarId, data);
      commit('addReminder', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateReminder({ commit }, { calendarId, reminderId, data }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await CalendarAPI.updateReminder(
        calendarId,
        reminderId,
        data
      );
      commit('updateReminder', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteReminder({ commit }, { calendarId, reminderId }) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await CalendarAPI.deleteReminder(calendarId, reminderId);
      commit('removeReminder', reminderId);
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  // ElevenLabs Voices
  async fetchElevenLabsVoices({ commit }) {
    commit('setUIFlag', { isFetchingVoices: true });
    try {
      const response = await CalendarAPI.getElevenLabsVoices();
      commit('setElevenLabsVoices', response.data.voices || []);
      return response.data.voices || [];
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingVoices: false });
    }
  },

  // Appointments
  async fetchAppointments({ commit }, params = {}) {
    console.log('[STORE ACTION DEBUG] fetchAppointments called with params:', params);
    commit('setUIFlag', { isFetchingAppointments: true });
    try {
      const response = await CalendarAPI.getAppointments(params);
      console.log('[STORE ACTION DEBUG] API response:', response.data?.length, 'appointments');
      console.log('[STORE ACTION DEBUG] Appointments by calendar:', 
        response.data?.reduce((acc, apt) => {
          const calId = apt.calendar?.id;
          acc[calId] = (acc[calId] || 0) + 1;
          return acc;
        }, {})
      );
      commit('setAppointments', response.data);
      return response.data;
    } catch (error) {
      console.error('[STORE ACTION DEBUG] Error fetching appointments:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingAppointments: false });
    }
  },

  async createAppointment({ commit }, data) {
    commit('setUIFlag', { isCreating: true });
    try {
      const response = await CalendarAPI.createAppointment(data);
      commit('addAppointment', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isCreating: false });
    }
  },

  async updateAppointment({ commit }, { appointmentId, data }) {
    commit('setUIFlag', { isUpdating: true });
    try {
      const response = await CalendarAPI.updateAppointment(appointmentId, data);
      commit('updateAppointment', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isUpdating: false });
    }
  },

  async deleteAppointment({ commit }, appointmentId) {
    commit('setUIFlag', { isDeleting: true });
    try {
      await CalendarAPI.deleteAppointment(appointmentId);
      commit('removeAppointment', appointmentId);
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isDeleting: false });
    }
  },

  async confirmAppointment({ commit }, appointmentId) {
    try {
      const response = await CalendarAPI.confirmAppointment(appointmentId);
      commit('updateAppointment', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async cancelAppointment({ commit }, { appointmentId, reason }) {
    try {
      const response = await CalendarAPI.cancelAppointment(appointmentId, reason);
      commit('updateAppointment', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async completeAppointment({ commit }, appointmentId) {
    try {
      const response = await CalendarAPI.completeAppointment(appointmentId);
      commit('updateAppointment', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async markNoShow({ commit }, appointmentId) {
    try {
      const response = await CalendarAPI.markNoShow(appointmentId);
      commit('updateAppointment', response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Availability
  async fetchAvailability({ commit }, { calendarId, date, agentId, appointmentTypeId }) {
    commit('setUIFlag', { isFetchingAvailability: true });
    try {
      const response = await CalendarAPI.getAvailability(calendarId, {
        date,
        agentId,
        appointmentTypeId,
      });
      commit('setAvailableSlots', response.data.slots);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingAvailability: false });
    }
  },

  // UI State
  setCurrentDate({ commit }, date) {
    commit('setCurrentDate', date);
  },

  setCurrentView({ commit }, view) {
    commit('setCurrentView', view);
  },

  // Calendar Selection (multi-select)
  setSelectedCalendarIds({ commit }, calendarIds) {
    commit('setSelectedCalendarIds', calendarIds);
    saveToStorage(SELECTED_CALENDARS_KEY, calendarIds);
  },

  toggleCalendarSelection({ commit, state: $state }, calendarId) {
    console.log('[STORE ACTION DEBUG] toggleCalendarSelection called for calendar:', calendarId);
    console.log('[STORE ACTION DEBUG] Current selected IDs before toggle:', $state.selectedCalendarIds);
    
    const currentIds = [...$state.selectedCalendarIds];
    const index = currentIds.indexOf(calendarId);
    if (index === -1) {
      console.log('[STORE ACTION DEBUG] Adding calendar to selection');
      currentIds.push(calendarId);
    } else {
      console.log('[STORE ACTION DEBUG] Removing calendar from selection');
      currentIds.splice(index, 1);
    }
    
    console.log('[STORE ACTION DEBUG] New selected IDs after toggle:', currentIds);
    commit('setSelectedCalendarIds', currentIds);
    saveToStorage(SELECTED_CALENDARS_KEY, currentIds);
  },

  selectAllCalendars({ commit, state: $state }) {
    const allIds = $state.calendars.map(c => c.id);
    commit('setSelectedCalendarIds', allIds);
    saveToStorage(SELECTED_CALENDARS_KEY, allIds);
  },

  deselectAllCalendars({ commit }) {
    commit('setSelectedCalendarIds', []);
    saveToStorage(SELECTED_CALENDARS_KEY, []);
  },

  // Agent Selection (multi-select)
  setSelectedAgentIds({ commit }, agentIds) {
    commit('setSelectedAgentIds', agentIds);
    saveToStorage(SELECTED_AGENTS_KEY, agentIds);
  },

  toggleAgentSelection({ commit, state: $state }, agentId) {
    const currentIds = [...$state.selectedAgentIds];
    const index = currentIds.indexOf(agentId);
    if (index === -1) {
      currentIds.push(agentId);
    } else {
      currentIds.splice(index, 1);
    }
    commit('setSelectedAgentIds', currentIds);
    saveToStorage(SELECTED_AGENTS_KEY, currentIds);
  },

  selectAllAgents({ commit }, allAgentIds) {
    commit('setSelectedAgentIds', allAgentIds);
    saveToStorage(SELECTED_AGENTS_KEY, allAgentIds);
  },

  deselectAllAgents({ commit }) {
    commit('setSelectedAgentIds', []);
    saveToStorage(SELECTED_AGENTS_KEY, []);
  },

  // Initialize selections when calendars are loaded (if localStorage is empty, select all)
  initializeSelections({ commit, state: $state }) {
    // If no calendars are selected and we have calendars, select all
    if ($state.selectedCalendarIds.length === 0 && $state.calendars.length > 0) {
      const allCalendarIds = $state.calendars.map(c => c.id);
      commit('setSelectedCalendarIds', allCalendarIds);
      saveToStorage(SELECTED_CALENDARS_KEY, allCalendarIds);
    } else {
      // Clean up any invalid calendar IDs (calendars that no longer exist)
      const validCalendarIds = $state.calendars.map(c => c.id);
      const cleanedIds = $state.selectedCalendarIds.filter(id => validCalendarIds.includes(id));
      if (cleanedIds.length !== $state.selectedCalendarIds.length) {
        commit('setSelectedCalendarIds', cleanedIds.length > 0 ? cleanedIds : validCalendarIds);
        saveToStorage(SELECTED_CALENDARS_KEY, cleanedIds.length > 0 ? cleanedIds : validCalendarIds);
      }
    }
  },
};

const mutations = {
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },

  // Calendars
  setCalendars($state, calendars) {
    $state.calendars = calendars;
  },

  setCurrentCalendar($state, calendar) {
    $state.currentCalendar = calendar;
  },

  addCalendar($state, calendar) {
    $state.calendars.push(calendar);
  },

  updateCalendar($state, calendar) {
    const index = $state.calendars.findIndex(c => c.id === calendar.id);
    if (index !== -1) {
      $state.calendars.splice(index, 1, calendar);
    }
    if ($state.currentCalendar?.id === calendar.id) {
      $state.currentCalendar = calendar;
    }
  },

  removeCalendar($state, calendarId) {
    $state.calendars = $state.calendars.filter(c => c.id !== calendarId);
    if ($state.currentCalendar?.id === calendarId) {
      $state.currentCalendar = null;
    }
  },

  // Appointment Types
  setAppointmentTypes($state, types) {
    $state.appointmentTypes = types;
  },

  addAppointmentType($state, type) {
    $state.appointmentTypes.push(type);
  },

  updateAppointmentType($state, type) {
    const index = $state.appointmentTypes.findIndex(t => t.id === type.id);
    if (index !== -1) {
      $state.appointmentTypes.splice(index, 1, type);
    }
  },

  removeAppointmentType($state, typeId) {
    $state.appointmentTypes = $state.appointmentTypes.filter(t => t.id !== typeId);
  },

  // Reminders
  setReminders($state, reminders) {
    $state.reminders = reminders;
  },

  addReminder($state, reminder) {
    $state.reminders.push(reminder);
  },

  updateReminder($state, reminder) {
    const index = $state.reminders.findIndex(r => r.id === reminder.id);
    if (index !== -1) {
      $state.reminders.splice(index, 1, reminder);
    }
  },

  removeReminder($state, reminderId) {
    $state.reminders = $state.reminders.filter(r => r.id !== reminderId);
  },

  // ElevenLabs Voices
  setElevenLabsVoices($state, voices) {
    $state.elevenLabsVoices = voices;
  },

  // Appointments
  setAppointments($state, appointments) {
    $state.appointments = appointments;
  },

  addAppointment($state, appointment) {
    $state.appointments.push(appointment);
  },

  updateAppointment($state, appointment) {
    const index = $state.appointments.findIndex(a => a.id === appointment.id);
    if (index !== -1) {
      $state.appointments.splice(index, 1, appointment);
    }
  },

  removeAppointment($state, appointmentId) {
    $state.appointments = $state.appointments.filter(a => a.id !== appointmentId);
  },

  // UI State
  setCurrentDate($state, date) {
    $state.currentDate = date;
  },

  setCurrentView($state, view) {
    $state.currentView = view;
  },

  setAvailableSlots($state, slots) {
    $state.availableSlots = slots;
  },

  // Selection state
  setSelectedCalendarIds($state, calendarIds) {
    $state.selectedCalendarIds = calendarIds;
  },

  setSelectedAgentIds($state, agentIds) {
    $state.selectedAgentIds = agentIds;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
